import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Check, X, Download } from 'lucide-react';
import { db } from '../../../firebase';
import { collection, query, orderBy, onSnapshot, deleteDoc, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import '../AdminPanel.css';

const SchedulesSection = () => {
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
    const [newBooking, setNewBooking] = useState({
        selectedDate: '',
        pstTime: '',
        name: '',
        email: '',
        company: '',
        country: '',
        status: 'scheduled'
    });

    useEffect(() => {
        const q = query(collection(db, 'appointments'), orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const appointmentData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setSchedules(appointmentData);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleStatusUpdate = async (id, newStatus) => {
        try {
            await updateDoc(doc(db, 'appointments', id), { status: newStatus });
        } catch (error) {
            console.error("Error updating status:", error);
        }
    };

    const handleAddBooking = async (e) => {
        e.preventDefault();
        try {
            await addDoc(collection(db, 'appointments'), {
                ...newBooking,
                createdAt: serverTimestamp()
            });
            setIsBookingModalOpen(false);
            setNewBooking({
                selectedDate: '',
                pstTime: '',
                name: '',
                email: '',
                company: '',
                country: '',
                status: 'scheduled'
            });
            alert("Booking added successfully!");
        } catch (error) {
            console.error("Error adding booking: ", error);
            alert("Failed to add booking.");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this appointment?")) {
            try {
                await deleteDoc(doc(db, 'appointments', id));
            } catch (error) {
                console.error("Error deleting appointment:", error);
            }
        }
    };

    const handleExportCSV = () => {
        if (schedules.length === 0) {
            alert("No schedules to export.");
            return;
        }

        const headers = ["ID", "Date", "Time", "Name", "Email", "Company", "Country", "Status"];
        const csvRows = [headers.join(',')];

        schedules.forEach(sch => {
            const row = [
                `"${sch.id}"`,
                `"${sch.selectedDate || ''}"`,
                `"${sch.pstTime || ''}"`,
                `"${sch.name || ''}"`,
                `"${sch.email || ''}"`,
                `"${sch.company || ''}"`,
                `"${sch.country || ''}"`,
                `"${sch.status || ''}"`
            ];
            csvRows.push(row.join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', `schedules_export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    return (
        <div className="panel active">
            <div className="page-hdr">
                <div className="page-hdr-left">
                    <div className="eyebrow">Management</div>
                    <h1>Schedules <span>.</span></h1>
                </div>
                <div className="page-hdr-actions">
                    <button className="btn btn-ghost" onClick={handleExportCSV}><Download size={14} /> Export CSV</button>
                    <button className="btn btn-primary" onClick={() => setIsBookingModalOpen(true)}><Calendar size={14} /> New Booking</button>
                </div>
            </div>

            <div className="tbl-wrap">
                <div className="tbl-scroll">
                    {loading ? (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Loading schedules...</div>
                    ) : (
                        <table>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Time</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Company</th>
                                    <th>Country</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {schedules.map(sch => (
                                    <tr key={sch.id}>
                                        <td className="muted">
                                            {sch.selectedDate ? new Date(sch.selectedDate).toLocaleDateString() : 'N/A'}
                                            <div className="text-xs">{sch.pstTime}</div>
                                        </td>
                                        <td style={{ fontWeight: 500 }}>
                                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                <Clock size={12} />
                                                {sch.selectedDate ? new Date(sch.selectedDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="font-bold">{sch.name}</div>
                                        </td>
                                        <td>
                                            <div className="text-muted">{sch.email}</div>
                                        </td>
                                        <td>
                                            <div>{sch.company || 'N/A'}</div>
                                        </td>
                                        <td>
                                            <div>{sch.country || 'N/A'}</div>
                                        </td>
                                        <td>
                                            <span className={`badge ${sch.status === 'confirmed' ? 'badge-organic' : 'badge-bing'}`}>
                                                {sch.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="action-btns">
                                                <button className="btn-ghost" style={{ padding: '4px', color: 'green' }} onClick={() => handleStatusUpdate(sch.id, 'confirmed')} title="Confirm"><Check size={14} /></button>
                                                <button className="btn-ghost" style={{ padding: '4px', color: 'red' }} onClick={() => handleDelete(sch.id)} title="Delete"><X size={14} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* ── NEW BOOKING MODAL ── */}
            {isBookingModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-[500px] shadow-xl">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold">New Booking</h3>
                            <button onClick={() => setIsBookingModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleAddBooking} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                    <input required type="date" className="w-full border rounded px-3 py-2" value={newBooking.selectedDate} onChange={e => setNewBooking({ ...newBooking, selectedDate: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Time (PST)</label>
                                    <input required type="time" className="w-full border rounded px-3 py-2" value={newBooking.pstTime} onChange={e => setNewBooking({ ...newBooking, pstTime: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                    <input required type="text" className="w-full border rounded px-3 py-2" value={newBooking.name} onChange={e => setNewBooking({ ...newBooking, name: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input required type="email" className="w-full border rounded px-3 py-2" value={newBooking.email} onChange={e => setNewBooking({ ...newBooking, email: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                                    <input type="text" className="w-full border rounded px-3 py-2" value={newBooking.company} onChange={e => setNewBooking({ ...newBooking, company: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                                    <input type="text" className="w-full border rounded px-3 py-2" value={newBooking.country} onChange={e => setNewBooking({ ...newBooking, country: e.target.value })} />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                                <button type="button" onClick={() => setIsBookingModalOpen(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-[#A2CB52] text-white rounded hover:bg-[#8bae46]">Create Booking</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SchedulesSection;
